import React, { useMemo } from "react";
import { FlagCircle } from "@/components/Flag";
import { kickoffToDate } from "@/lib/kickoffTime";
import { effectiveMatchScores } from "@/lib/matchScores";

function byKickoffAsc(a, b) {
  const ta = kickoffToDate(a.kickoff).getTime();
  const tb = kickoffToDate(b.kickoff).getTime();
  return ta - tb;
}

function resultCell(m, t) {
  const s = effectiveMatchScores(m);
  if ((m.status === "FINISHED" || m.status === "LIVE") && s) {
    return (
      <span className={`font-mono font-semibold tabular-nums ${m.status === "LIVE" ? "text-danger" : "text-[var(--accent-2)]"}`}>
        {s.home}–{s.away}
      </span>
    );
  }
  if (m.status === "LIVE") {
    return (
      <span className="inline-flex items-center gap-1 text-danger text-[10px] uppercase font-semibold">
        <span className="live-dot" />
        {t("project.matchLive")}
      </span>
    );
  }
  return <span className="text-[var(--text-dim)]">–</span>;
}

/**
 * Compact group-stage match list with live/final scores (Tabeller tab).
 */
export function GroupStandingsMatchList({ matches, teamsByCode, logoByCode = {}, lang, t }) {
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const sorted = useMemo(() => [...(matches || [])].sort(byKickoffAsc), [matches]);

  if (!sorted.length) return null;

  return (
    <div className="mt-4 pt-3 border-t border-[var(--line)]">
      <div className="text-[var(--text-dim)] uppercase tracking-wider text-[10px] mb-2">
        {t("standings.groupMatches")}
      </div>
      <ul className="space-y-0 divide-y divide-[var(--line)]">
        {sorted.map((m) => {
          const ko = kickoffToDate(m.kickoff);
          const dateStr = ko.toLocaleDateString(loc, { day: "numeric", month: "short" });
          const timeStr = ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
          const home = m.home_code ? teamsByCode[m.home_code] : m.home_placeholder;
          const away = m.away_code ? teamsByCode[m.away_code] : m.away_placeholder;
          return (
            <li key={m.id} className="py-2 first:pt-0">
              <div className="text-[10px] font-mono text-[var(--text-dim)] mb-1">
                {dateStr} · {timeStr}
                {m.status === "FINISHED" && (
                  <span className="ml-1.5 uppercase text-[var(--accent-2)]">{t("project.matchFinished")}</span>
                )}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex min-w-0 flex-1 items-center gap-1 text-xs">
                  <FlagCircle code={m.home_code} size={18} logoUrl={logoByCode[m.home_code]} />
                  <span className="min-w-0 truncate" title={home}>{home}</span>
                  <span className="shrink-0 text-[var(--text-dim)] px-0.5">–</span>
                  <span className="min-w-0 truncate" title={away}>{away}</span>
                  <FlagCircle code={m.away_code} size={18} logoUrl={logoByCode[m.away_code]} />
                </div>
                <div className="shrink-0 text-xs text-right min-w-[3.25rem]">{resultCell(m, t)}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
